/**
 * New Year embroidery patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearEmbroideryPatternsPack: ToilePack = {
    slug: 'new-year-embroidery-patterns',
    category: 'season',
    name: 'New Year embroidery patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'firework', label: 'Firework', subject: 'A firework burst with trailing sparks'},
        {key: 'bottle', label: 'Champagne bottle', subject: 'A champagne bottle with a popping cork and streaming ribbon'},
        {key: 'stars', label: 'Star wreath', subject: 'A round wreath of stars and small sparks'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['a clock instead', 'as a hoop border', 'smaller for a bookmark'],
    directionHint: 'try: a clock instead',
    seo: {
        title: 'New Year embroidery: firework, bottle, stars',
        metaDescription: 'Nine New Year embroidery transfer patterns — a firework, a champagne bottle, a star wreath — in simple, detailed or doodle stitch guides.',
        h1: 'New Year embroidery patterns: firework, bottle and star wreath',
        tagline: 'Nine transfer patterns, three celebration motifs, three stitch-guide styles each — ready to trace onto fabric.',
        intro: 'An embroidery transfer pattern for midnight: a firework burst with trailing sparks, a champagne bottle with a popping cork, a round wreath of stars. Each is drawn as a bold simple outline, a detailed pattern with fine decorative guides, or a loose doodle-style line. Fabric-free black-on-white line art, ready to trace. Ask for a different motif, like a clock, and it redraws.',
        useCases: [
            'a transfer pattern for the embroidery hoop',
            "a New Year's Eve table-linen design",
            'a small celebration gift for a fellow stitcher',
            "a beginner's first celebration hoop project",
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each pattern is centered on a square page with a wide white margin, sized to trace onto standard hoop fabric.'},
            {q: 'Do I need special transfer paper?', a: 'Any iron-on transfer pencil or light-box tracing method works — the pattern is clean black line art on white, ready either way.'},
            {q: 'Can I get a different celebration motif?', a: "Yes — that's what the direction field is for. Type a different motif, like a clock or a hoop border, and it redraws in the same three styles."},
        ],
    },
}
